import { getStatusClass, getStatusLabel } from "./statusUtils.js";
import { REASON_CONFIG, RESULT_CONFIG, STATUS_CONFIG } from "./configStatus.js";

export const resultCode = (value) => typeof value === "string" && /^[A-Z_]{1,64}$/.test(value.trim().toUpperCase())
  ? value.trim().toUpperCase() : "";

const REVIEW_LABELS = {
  BUSY_EVIDENCE_MISSING: "Occupation non confirmée — qualification à vérifier",
  CALLBACK_DATE_MISSING: "Rappel demandé — date à confirmer",
  CALLBACK_TIME_MISSING: "Rappel demandé — heure à confirmer",
  MISSING_CALLBACK_TIME: "Rappel demandé — heure à confirmer",
  CALLBACK_TEMPORAL_AMBIGUOUS: "Rappel demandé — date ou heure ambiguë",
  CALLBACK_TEMPORAL_CONFLICT: "Rappel demandé — date et heure à vérifier",
  CALLBACK_TEMPORAL_INVALID: "Rappel demandé — date ou heure invalide",
  CALLBACK_OUTSIDE_CAMPAIGN_WINDOW: "Rappel demandé hors horaires de campagne — à organiser",
  OUTSIDE_CAMPAIGN_WINDOW: "Rappel demandé hors horaires de campagne — à organiser",
  QUALIFICATION_UNCERTAIN: "Résultat de l’échange à vérifier",
  EMAIL_REQUESTED: "Demande d’email — envoi non confirmé",
  DECISION_MAKER_UNAVAILABLE: "Interlocuteur absent ou non décisionnaire — suite à vérifier",
  AUTOMATED_WAITING: "Accueil ou attente annoncée — résultat à vérifier",
  INCOMPLETE_EXCHANGE: "Échange incomplet — aucun résultat commercial établi",
  TRANSCRIPT_UNCERTAIN: "Paroles ou attribution incertaines — qualification à vérifier",
  CONTACT_REDIRECTION: "Orientation vers un autre contact — suite à vérifier",
};

export const hasOpposition = (record = {}) => {
  const ai = record.aiResponse || {};
  return ai.opposition === true || Number(ai.blacklist) === 2 || resultCode(ai.status) === "NPA";
};

export const getHistoryCategory = (record = {}) => {
  const ai = record.aiResponse || {};
  if (hasOpposition(record)) return "npa";
  if ((Number(record.status) === 4 && resultCode(ai.status) === "OCCUPE" && Number(ai.telephonyCause) !== 17)
    || ((record.status == null || (Number(record.status) === 3 && resultCode(ai.status) === "CALLBACK"))
      && (ai.manualReview?.required === true
        || Object.hasOwn(REVIEW_LABELS, resultCode(ai.technicalError?.code))))) return "review";
  return record.status == null ? "unqualified" : String(record.status);
};

export const getAnalysisDiagnostic = (record = {}) => {
  const ai = record.aiResponse || {};
  const error = ai.technicalError;
  const structuredError = error && typeof error === "object" && !Array.isArray(error)
    && (resultCode(error.code) || resultCode(error.stage));
  const status = resultCode(ai.status);
  const manualReason = resultCode(ai.manualReview?.reasonCode);
  if (ai.manualReview?.required === true && REVIEW_LABELS[manualReason] && !hasOpposition(record)) {
    return { label: REVIEW_LABELS[manualReason], tone: "warning", technical: false };
  }
  if (error === true || structuredError || status === "TECHNICAL_ERROR") {
    const code = resultCode(error?.code || ai.errorCode);
    if (REVIEW_LABELS[code]) return { label: REVIEW_LABELS[code], tone: "warning", technical: false };
    if (code === "UNCLASSIFIED_TELEPHONY_END") {
      return { label: "Fin d’appel téléphonique non déterminée — résultat à vérifier", tone: "warning", technical: true };
    }
    if (code === "EMPTY_TRANSCRIPTION") {
      return { label: "Aucune parole transcrite — qualification indisponible", tone: "warning", technical: true };
    }
    if (code === "AUDIO_EMPTY") {
      return { label: "Enregistrement audio vide — qualification indisponible", tone: "warning", technical: true };
    }
    if (code === "AUDIO_NO_SIGNAL") {
      return { label: "Aucun signal audio exploitable dans l’enregistrement — qualification indisponible", tone: "warning", technical: true };
    }
    if (code === "CALL_SETUP_TIMEOUT") {
      return { label: "Délai d’établissement de l’appel dépassé — résultat à vérifier", tone: "warning", technical: true };
    }
    if (code === "RECORDING_TOO_SHORT") {
      const metrics = ai.diagnostics?.recording;
      const recorded = metrics?.recordedDurationSeconds, expected = metrics?.expectedSessionDurationSeconds;
      const valid = typeof recorded === "number" && Number.isFinite(recorded) && recorded >= 0 && recorded <= 86400
        && typeof expected === "number" && Number.isFinite(expected) && expected > 0 && expected <= 86400;
      const seconds = (value) => value.toLocaleString("fr-FR", { maximumFractionDigits: 1 });
      return { label: "Enregistrement trop court pour la session" + (valid
        ? ` (${seconds(recorded)} s enregistrées / ${seconds(expected)} s de session)` : ""), tone: "danger", technical: true };
    }
    return { label: "Erreur technique d’analyse", tone: "danger", technical: true };
  }
  if (getHistoryCategory(record) === "review") {
    return { label: status === "OCCUPE" ? "Occupation non confirmée — qualification à vérifier" : "Qualification à vérifier", tone: "warning", technical: false };
  }
  if (status === "CALLBACK" && (!ai.dateCLB || !ai.heureCallBack)) {
    return { label: ai.dateCLB ? "Rappel demandé — heure à confirmer" : "Rappel demandé — date à confirmer", tone: "warning", technical: false };
  }
  // A call without an answer has a telephony outcome, not a business qualification.
  // Keep any stored qualification and audio available instead of fabricating either.
  if (status === "NO_ANSWER") {
    return { label: record.pathRecord ? "Sans réponse" : "Sans réponse — aucun échange enregistré",
      tone: "secondary", technical: false };
  }
  const state = resultCode(ai.analysisState);
  if (["FAILED", "ERROR"].includes(state)) return { label: "Analyse indisponible", tone: "danger", technical: true };
  if (["PENDING", "PROCESSING"].includes(state)) return { label: "Analyse en cours", tone: "info", technical: true };
  if (["UNKNOWN", "ORIGINATE_UNCERTAIN"].includes(status)) return { label: "Résultat à vérifier", tone: "warning", technical: true };
  return null;
};

export const getRecordingAvailabilityLabel = (record = {}) => {
  if (record.pathRecord) return null;
  const ai = record.aiResponse || {};
  if (resultCode(ai.status) === "NO_ANSWER") return "Aucun échange enregistré (sans réponse).";
  if (["UNCLASSIFIED_TELEPHONY_END", "CALL_SETUP_TIMEOUT"].includes(resultCode(ai.technicalError?.code || ai.errorCode))) {
    return "Aucun échange enregistré pour cette tentative.";
  }
  return "Aucun fichier audio disponible.";
};

export const getReviewEvidence = (record = {}) => {
  const ai = record.aiResponse || {}, proof = ai.reviewEvidence;
  if (hasOpposition(record) || ai.manualReview?.required !== true || !proof || proof.version !== 1
    || !Object.hasOwn(REVIEW_LABELS, resultCode(proof.reasonCode))
    || typeof proof.quote !== "string" || !proof.quote.trim() || proof.quote.length > 500
    || /[\u0000-\u0008\u000b\u000c\u000e-\u001f]/.test(proof.quote)) return null;
  if (proof.source === "live_remote_transcription" && proof.speaker === "remote_line" && proof.attributed === true) {
    return { quote: proof.quote, sourceLabel: "Extrait transcrit de la ligne distante" };
  }
  if (proof.source === "mixed_saved_transcription" && proof.speaker === "unknown" && proof.attributed === false) {
    return { quote: proof.quote, sourceLabel: "Extrait transcrit — interlocuteur non confirmé" };
  }
  return null;
};

// Derived presentation never rewrites stored historical qualifications.
export const getHistoryPresentation = (record = {}) => {
  const diagnostic = getAnalysisDiagnostic(record);
  const category = getHistoryCategory(record);
  const derived = category === "npa" || category === "review";
  return { diagnostic, category, derived, opposition: hasOpposition(record),
    qualification: category === "npa" ? "Ne plus appeler" : category === "review" ? "À vérifier"
      : record.status == null ? "Non renseignée" : getStatusLabel(record.status),
    qualificationClass: category === "npa" ? getStatusClass(1) : diagnostic || derived ? "default" : getStatusClass(record.status) };
};

export const getAppointment = (record = {}) => {
  const ai = record.aiResponse || {};
  if (Number(record.status) !== 2 || resultCode(ai.status) !== "SALE" || getAnalysisDiagnostic(record) || hasOpposition(record)) return null;
  if (typeof ai.dateRDV !== "string" || typeof ai.heureRDV !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(ai.dateRDV)
      || !/^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(ai.heureRDV)) return null;
  const date = new Date(ai.dateRDV + "T12:00:00Z");
  if (!Number.isFinite(+date) || date.toISOString().slice(0, 10) !== ai.dateRDV) return null;
  return { date: ai.dateRDV, time: ai.heureRDV };
};

export const getScheduledPresentation = (call = {}) => {
  const requiresReview = call.requiresReview === true || call.status === "indeterminate";
  const reason = REASON_CONFIG[resultCode(call.reason)] || { label: "Motif non renseigné", color: "reason-neutral", icon: "bi-question-circle" };
  const code = resultCode(call.resultStatus);
  const result = code ? RESULT_CONFIG[code] || { label: "Résultat non reconnu", color: "reason-neutral" } : null;
  let status = STATUS_CONFIG[call.status] || { label: "État non reconnu", color: "status-neutral", icon: "bi-question-circle" };
  if (requiresReview) status = { label: call.reviewReason === "HELD" ? "Sous garde" : "Exécution à vérifier", color: "status-review", icon: "bi-shield-lock" };
  else if (code === "SKIPPED") status = { label: "Non exécuté", color: "status-neutral", icon: "bi-skip-forward" };
  const reasonLabel = resultCode(call.reason) === "NI"
    ? (Number(call.source) === 2 ? "Suivi manuel : pas intéressé" : "Suivi automatique : pas intéressé")
    : reason.label;
  return { status, reason: { ...reason, label: reasonLabel }, result, requiresReview };
};

export const scheduledMutationError = (error, action = "modifier") =>
  error?.response?.status === 409
    ? "Ce rappel est protégé. Consultez le diagnostic des rappels avant toute modification."
    : `Impossible de ${action} ce rappel. Réessayez après vérification.`;
